// Burbuja flotante del asistente de la crisis. Aparece SOLO cuando estás dentro
// de una crisis (/crisis/:id), arriba de la burbuja del Asistente Arcana. Cada
// crisis tiene su propia conversación (el historial se resetea al cambiar de id).
//
// Se monta una vez a nivel global (App.js); ella misma decide si mostrarse según
// la ruta. Al tocarla abre un panel de chat que conoce todo el panel de la
// crisis (cobertura, humor social, plan, kit, desenlace).

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  FaComments, FaPaperPlane, FaRobot, FaUser, FaSpinner, FaRegCopy, FaTimes,
} from 'react-icons/fa';
import { TechInput, TechButton, mono, borderSoft } from '../../styles/techComponents';
import { chatCrisis } from '../../services/crisisService';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 71, 111, 0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(239, 71, 111, 0); }
`;

// Arriba de la burbuja del Asistente Arcana (que está en bottom:24px).
const Launcher = styled.button`
  position: fixed; bottom: 88px; right: 24px; z-index: 1000;
  display: flex; align-items: center; gap: 0.7rem;
  padding: 0.7rem 1rem; border-radius: 999px;
  border: 1px solid rgba(239, 71, 111, 0.4);
  background: linear-gradient(135deg, rgba(239, 71, 111, 0.16), rgba(11, 19, 32, 0.92) 60%), rgba(11, 19, 32, 0.92);
  color: var(--text-primary); backdrop-filter: blur(10px); cursor: pointer;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.45);
  transition: transform 0.18s ease, border-color 0.18s ease;
  font-family: inherit;
  &:hover { transform: translateY(-2px); border-color: rgba(239, 71, 111, 0.7); }
`;

const IconWrap = styled.span`
  position: relative; display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 50%;
  background: rgba(239, 71, 111, 0.14); color: var(--alert-error);
  border: 1px solid rgba(239, 71, 111, 0.35); flex-shrink: 0;
`;
const Dot = styled.span`
  position: absolute; top: -1px; right: -1px; width: 8px; height: 8px; border-radius: 50%;
  background: var(--alert-error); border: 2px solid #0b1320; animation: ${pulse} 2s ease-in-out infinite;
`;
const LabelWrap = styled.span` display: flex; flex-direction: column; line-height: 1.1; text-align: left; `;
const Label = styled.span` font-size: 0.85rem; font-weight: 600; color: var(--text-primary); `;
const Tag = styled.span`
  font-family: ${mono}; font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase;
  color: var(--alert-error); margin-top: 2px;
`;

// Panel de chat que se abre al tocar la burbuja.
const Panel = styled.div`
  position: fixed; bottom: 88px; right: 24px; z-index: 1001;
  width: min(420px, calc(100vw - 32px)); max-height: min(620px, calc(100vh - 120px));
  display: flex; flex-direction: column;
  background: var(--panel-bg, #0f172a); border: 1px solid rgba(239, 71, 111, 0.4);
  border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6);
  overflow: hidden;
`;
const Cab = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-bottom: 1px solid ${borderSoft};
  background: linear-gradient(135deg, rgba(239, 71, 111, 0.1), transparent 60%);
`;
const CabTitulo = styled.div`
  font-size: 0.9rem; font-weight: 600; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px;
  span.t { font-size: 0.7rem; color: var(--text-secondary); font-weight: 400;
    max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;
const Cerrar = styled.button`
  background: none; border: none; color: var(--text-secondary); cursor: pointer;
  font-size: 0.95rem; padding: 4px; display: flex; border-radius: 4px;
  &:hover { color: var(--text-primary); background: rgba(255,255,255,0.06); }
`;
const Hilo = styled.div`
  flex: 1; overflow-y: auto; padding: 14px;
  display: flex; flex-direction: column; gap: 12px;
`;
const Burbuja = styled.div`
  display: flex; gap: 9px; align-items: flex-start;
  .icono { flex-shrink: 0; margin-top: 2px; font-size: 0.74rem;
    color: ${(p) => (p.$ia ? 'var(--btn-primary)' : 'var(--text-secondary)')}; }
  .texto { font-size: 0.86rem; line-height: 1.6; white-space: pre-wrap; min-width: 0; word-break: break-word;
    color: ${(p) => (p.$ia ? 'var(--text-primary)' : 'var(--text-secondary)')}; }
`;
const Vacio = styled.p` margin: 0; font-size: 0.85rem; color: var(--text-secondary); line-height: 1.55; `;
const Pensando = styled.div`
  font-size: 0.82rem; color: var(--text-secondary); display: flex; align-items: center; gap: 8px;
  svg { animation: girar 1s linear infinite; } @keyframes girar { to { transform: rotate(360deg); } }
`;
const Atajos = styled.div` display: flex; gap: 6px; flex-wrap: wrap; padding: 0 14px 10px; `;
const Atajo = styled.button`
  font-family: ${mono}; font-size: 0.68rem; color: var(--text-secondary);
  background: rgba(255,255,255,0.04); border: 1px solid ${borderSoft}; border-radius: 5px;
  padding: 4px 8px; cursor: pointer;
  &:hover { color: var(--btn-primary); border-color: rgba(0,180,216,0.4); } &:disabled { opacity: 0.5; }
`;
const Pie = styled.div`
  display: flex; gap: 8px; padding: 10px 14px 14px; border-top: 1px solid ${borderSoft};
  > input { flex: 1; }
`;
const Copiar = styled.button`
  font-family: ${mono}; font-size: 0.66rem; color: var(--text-secondary);
  background: rgba(255,255,255,0.04); border: 1px solid ${borderSoft}; border-radius: 4px;
  padding: 2px 7px; cursor: pointer; margin-top: 5px; display: inline-flex; align-items: center; gap: 4px;
  &:hover { color: var(--btn-primary); }
`;

const ATAJOS = [
  '¿Cómo venimos?',
  '¿Qué hago ahora?',
  '¿A favor o en contra?',
  'Resumime todo',
];

function PanelChat({ crisisId, onCerrar }) {
  const [chat, setChat] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [pensando, setPensando] = useState(false);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(null);
  const hiloRef = useRef(null);

  const scroll = () => setTimeout(() => {
    if (hiloRef.current) hiloRef.current.scrollTop = hiloRef.current.scrollHeight;
  }, 50);

  const preguntar = async (texto) => {
    const q = (texto ?? mensaje).trim();
    if (q.length < 2 || pensando) return;
    setPensando(true); setError(null);
    const previo = chat;
    setChat((c) => [...c, { role: 'user', content: q }]);
    setMensaje(''); scroll();
    try {
      const r = await chatCrisis(crisisId, q, previo);
      setChat((c) => [...c, { role: 'assistant', content: r.respuesta }]); scroll();
    } catch {
      setError('No se pudo responder. Probá de nuevo.');
      setChat(previo);
    } finally { setPensando(false); }
  };

  const copiar = async (t, i) => {
    try { await navigator.clipboard.writeText(t); setCopiado(i); setTimeout(() => setCopiado(null), 1600); }
    catch { /* sin portapapeles */ }
  };

  return (
    <Panel role="dialog" aria-label="Asistente de la crisis">
      <Cab>
        <CabTitulo><FaRobot style={{ color: 'var(--alert-error)' }} /> Asistente de la crisis</CabTitulo>
        <Cerrar onClick={onCerrar} aria-label="Cerrar"><FaTimes /></Cerrar>
      </Cab>

      <Hilo ref={hiloRef}>
        {chat.length === 0 ? (
          <Vacio>
            Conozco todo sobre esta crisis: la cobertura, el humor de la gente, el plan, el kit
            y cómo viene. Preguntame lo que quieras.
          </Vacio>
        ) : chat.map((m, i) => (
          <Burbuja key={i} $ia={m.role === 'assistant'}>
            <span className="icono">{m.role === 'assistant' ? <FaRobot /> : <FaUser />}</span>
            <div className="texto">
              {m.content}
              {m.role === 'assistant' && (
                <div><Copiar onClick={() => copiar(m.content, i)}><FaRegCopy /> {copiado === i ? 'copiado' : 'copiar'}</Copiar></div>
              )}
            </div>
          </Burbuja>
        ))}
        {pensando && <Pensando><FaSpinner /> Pensando…</Pensando>}
        {error && <Vacio style={{ color: 'var(--alert-error)' }}>{error}</Vacio>}
      </Hilo>

      {chat.length === 0 && (
        <Atajos>
          {ATAJOS.map((a) => <Atajo key={a} onClick={() => preguntar(a)} disabled={pensando}>{a}</Atajo>)}
        </Atajos>
      )}

      <Pie>
        <TechInput
          placeholder="Preguntá sobre la crisis…"
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') preguntar(); }}
          disabled={pensando}
          autoFocus
        />
        <TechButton onClick={() => preguntar()} disabled={pensando || mensaje.trim().length < 2}>
          <FaPaperPlane />
        </TechButton>
      </Pie>
    </Panel>
  );
}

export default function ChatCrisis() {
  const location = useLocation();
  const [abierto, setAbierto] = useState(false);

  // Aparece SOLO en la sala de una crisis (/crisis/:id, id numérico).
  const match = location.pathname.match(/^\/crisis\/(\d+)$/);
  const crisisId = match ? match[1] : null;

  // Al cambiar de crisis (o salir), se cierra el panel — cada crisis es su
  // propia conversación (el `key` de abajo recrea el chat con historial nuevo).
  useEffect(() => { setAbierto(false); }, [crisisId]);

  if (!crisisId) return null;

  if (abierto) {
    return <PanelChat key={crisisId} crisisId={crisisId} onCerrar={() => setAbierto(false)} />;
  }

  return (
    <Launcher type="button" onClick={() => setAbierto(true)} aria-label="Abrir asistente de la crisis">
      <IconWrap><FaComments size={14} /><Dot /></IconWrap>
      <LabelWrap>
        <Label>Asistente de la crisis</Label>
        <Tag>IA · Esta crisis</Tag>
      </LabelWrap>
    </Launcher>
  );
}
